import React from "react";
import { Link } from "react-router-dom";
import { UserCircle, Wine, Camera } from "@phosphor-icons/react";
import ArtDecoDivider from "../components/ArtDecoDivider";

// Moody hero background — atmospheric only, clearly not a photo of him.
const HERO_BG =
    "https://images.unsplash.com/photo-1615887625746-f3d2aa27e048?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHx3aGlza2V5JTIwZ2xhc3MlMjBkYXJrfGVufDB8fHx8MTc3OTQ2OTYwMXww&ixlib=rb-4.1.0&q=85";

// Portrait / action shot placeholders. Replace src with real image URLs when photos land.
function PhotoSlot({ label, aspect = "aspect-[4/5]", testid, icon: Icon = Camera }) {
    return (
        <div
            data-testid={testid}
            className={`relative ${aspect} bg-obsidian-soft border border-white/10 overflow-hidden flex items-center justify-center`}
        >
            <div className="absolute inset-0 grain-bg opacity-40" aria-hidden="true" />
            <div className="relative text-center px-6">
                <Icon size={40} weight="thin" className="text-gold/50 mx-auto mb-3" />
                <div className="text-white/40 text-xs tracking-overline uppercase">
                    {label}
                </div>
                <div className="text-white/25 text-[10px] tracking-wide uppercase mt-1">
                    Awaiting Photo
                </div>
            </div>
        </div>
    );
}

const CRAFT_TENETS = [
    {
        title: "The First Sip",
        body: "There's a look people get right after that first pull — brow drops, eyes widen slightly, small nod. That's the moment I'm chasing every time.",
    },
    {
        title: "Liquid Painkiller",
        body: "Some nights you don't need a menu, you need a bartender. I'm the guy you can walk up to when you're stuck for a drink — or stuck on a decision — and get an honest answer from someone who's been around the block.",
    },
    {
        title: "No Drink Kept Waiting",
        body: "I move fast. Every drink is built properly and gets to whoever ordered it while it's still cold. Restless behind the bar, calm out front — that's the deal.",
    },
];

export default function About() {
    return (
        <div className="bg-obsidian min-h-screen" data-testid="page-about">
            {/* HERO */}
            <section className="relative h-[70vh] min-h-[520px] flex items-end overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={HERO_BG}
                        alt=""
                        className="w-full h-full object-cover opacity-50"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/70 to-obsidian/30" />
                </div>
                <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-12 w-full pb-16">
                    <div className="text-gold text-xs tracking-overline uppercase mb-4 flex items-center gap-3">
                        <span className="w-10 h-px bg-gold" />
                        Meet the Bartender
                    </div>
                    <h1
                        className="font-display text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-3xl"
                        data-testid="about-hero-title"
                    >
                        Behind the <em className="gold-italic">Bar</em>
                    </h1>
                    <p className="text-white/70 text-lg md:text-xl font-light max-w-2xl mt-6">
                        A short story about who's actually pouring — because you should know.
                    </p>
                </div>
            </section>

            {/* THE STORY — editorial two-column */}
            <section className="py-24 px-6 lg:px-12">
                <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                    {/* Portrait */}
                    <div className="lg:col-span-5" data-testid="about-portrait">
                        <PhotoSlot label="Portrait" testid="photo-slot-portrait" icon={UserCircle} />
                        <div className="mt-4 text-xs tracking-overline uppercase text-white/40 text-center">
                            The Spirited Gentleman
                        </div>
                    </div>

                    {/* Story */}
                    <div className="lg:col-span-7">
                        <div className="text-gold text-xs tracking-overline uppercase mb-3 flex items-center gap-3">
                            <span className="font-display italic text-2xl text-gold/60">01</span>
                            <span className="w-8 h-px bg-gold/50" />
                            The Story
                        </div>
                        <h2 className="font-display text-white text-4xl md:text-5xl mb-8 leading-tight">
                            It started with <em className="gold-italic">free golf</em>
                        </h2>

                        <div className="space-y-5 text-white/75 font-light text-base md:text-lg leading-relaxed">
                            <p>
                                I learned to bartend at a private country club in Wallingford,
                                Connecticut. If I'm being honest, I mostly took the job because
                                employees played the course for free on Mondays. Turns out, I was
                                a better bartender than golfer.
                            </p>
                            <p>
                                From there I chased the trade around the country — Connecticut
                                to Vermont, out to Las Vegas, and eventually to Southern
                                California, where I ran the members bar at the prestigious{" "}
                                <span className="text-gold">Old Ranch Country Club</span>. Every
                                room taught me something. The country clubs taught me service.
                                Vegas taught me speed. Vermont taught me patience. California
                                taught me how to make a party feel effortless.
                            </p>
                            <p>
                                Somewhere in the middle of all that, I stopped counting the
                                weddings. Easily <span className="text-gold">a thousand-plus</span> —
                                everything from backyard ceremonies with sixty guests to the kind
                                of black-tie affair where the bride and groom rode in{" "}
                                <em className="gold-italic">on an elephant</em>. Setting a satellite
                                bar has become muscle memory at this point; I could build one in
                                my sleep, and honestly, I probably have.
                            </p>
                            <p>
                                A little flair still slips out when the room asks for it — a
                                bottle flip, a toss, a piece of theater — but that's not what I
                                sell. What I sell is the drink itself, and the way it lands.
                            </p>
                        </div>

                        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="border-l border-gold/40 pl-4">
                                <div className="text-xs tracking-overline uppercase text-white/40 mb-1">
                                    Origin
                                </div>
                                <div className="font-display text-white text-lg">Wallingford, CT</div>
                            </div>
                            <div className="border-l border-gold/40 pl-4">
                                <div className="text-xs tracking-overline uppercase text-white/40 mb-1">
                                    Sharpened In
                                </div>
                                <div className="font-display text-white text-lg">Vegas · Vermont</div>
                            </div>
                            <div className="border-l border-gold/40 pl-4">
                                <div className="text-xs tracking-overline uppercase text-white/40 mb-1">
                                    Ran the Bar
                                </div>
                                <div className="font-display text-white text-lg">Old Ranch, CA</div>
                            </div>
                            <div className="border-l border-gold/40 pl-4">
                                <div className="text-xs tracking-overline uppercase text-white/40 mb-1">
                                    Weddings Worked
                                </div>
                                <div className="font-display text-white text-lg">1,000+</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION DIVIDER */}
            <div className="max-w-6xl mx-auto px-6 lg:px-12">
                <ArtDecoDivider />
            </div>

            {/* WHAT HE BRINGS — three tenets */}
            <section className="py-20 px-6 lg:px-12">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <div className="text-gold text-xs tracking-overline uppercase mb-3 flex items-center justify-center gap-3">
                            <span className="font-display italic text-2xl text-gold/60">02</span>
                            <span className="w-8 h-px bg-gold/50" />
                            Behind the Craft
                        </div>
                        <h2 className="font-display text-white text-4xl md:text-5xl">
                            What I <em className="gold-italic">bring</em>
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5">
                        {CRAFT_TENETS.map((t, idx) => (
                            <div
                                key={t.title}
                                data-testid={`tenet-${idx + 1}`}
                                className="bg-obsidian-soft p-8 md:p-10 hover:bg-[#191919] transition-colors duration-500"
                            >
                                <div className="font-display italic text-gold/60 text-3xl mb-4">
                                    {String(idx + 1).padStart(2, "0")}
                                </div>
                                <h3 className="font-display text-white text-2xl mb-4">{t.title}</h3>
                                <p className="text-white/60 font-light leading-relaxed">{t.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* GALLERY — action shot placeholders */}
            <section className="py-16 px-6 lg:px-12">
                <div className="max-w-6xl mx-auto">
                    <div className="flex items-baseline justify-between mb-8">
                        <div>
                            <div className="text-gold text-xs tracking-overline uppercase mb-2 flex items-center gap-3">
                                <span className="font-display italic text-2xl text-gold/60">03</span>
                                <span className="w-8 h-px bg-gold/50" />
                                From The Bar
                            </div>
                            <h2 className="font-display text-white text-3xl md:text-4xl">
                                Nights on the <em className="gold-italic">clock</em>
                            </h2>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <PhotoSlot label="Action Shot 1" aspect="aspect-[3/4]" testid="photo-slot-1" icon={Wine} />
                        <PhotoSlot label="Action Shot 2" aspect="aspect-[3/4]" testid="photo-slot-2" icon={Wine} />
                        <PhotoSlot label="Action Shot 3" aspect="aspect-[3/4]" testid="photo-slot-3" icon={Wine} />
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 px-6 lg:px-12 text-center border-t border-white/5 mt-8">
                <div className="text-gold text-xs tracking-overline uppercase mb-4">
                    Now You've Met Me
                </div>
                <h2 className="font-display text-white text-4xl md:text-5xl mb-8">
                    Let's plan an <em className="gold-italic">evening</em>
                </h2>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/book" className="btn-gold" data-testid="about-cta-reserve">
                        Reserve The Evening
                    </Link>
                    <Link to="/beyond" className="btn-gold-outline" data-testid="about-cta-beyond">
                        Beyond the Bar
                    </Link>
                </div>
            </section>
        </div>
    );
}
